// 🔁 Core imports
import React, { useState, useEffect, useCallback, useRef } from 'react';
import JobForm from '../components/JobForm';
import FilterBar from '../components/FilterBar';
//import JobDescriptionEditor from '../components/JobDescriptionEditor';
import { useNavigate } from 'react-router-dom';
import useAdminAutoLogout from '../hooks/useAdminAutoLogout';
import { API_BASE_URL } from '../config/apiConfig';
//import ReactQuill from 'react-quill';
import './AdminDashboard.css';

function AdminDashboard({ token, onPostSuccess }) {
  // 👤 Get logged-in admin username
  const username = localStorage.getItem('adminUser') || 'Admin';
  const navigate = useNavigate();
  useEffect(() => {
    const expiry = localStorage.getItem('adminTokenExpiry');
    const isExpired = expiry && Date.now() > Number(expiry);

    if (!token || isExpired) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpiry');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
    }
  }, [token, navigate]);

  const { warningVisible } = useAdminAutoLogout(); // 🕒 Auto logout warning

  // 🚪 Logout logic
  /*const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };*/


  // 📊 Admin dashboard stats (total jobs + recent posts)
  const [stats, setStats] = useState({ totalJobs: 0, recentJobs: [] });
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/jobs/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setStats({
          totalJobs: data.totalJobs || 0,
          recentJobs: Array.isArray(data.recentJobs) ? data.recentJobs : []
        });
      })
      .catch(() => setStats({ totalJobs: 0, recentJobs: [] }));
  }, [token]);

  // 📋 All job posts with pagination + editing
  const [jobs, setJobs] = useState([]);
  const [editJob, setEditJob] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const jobFormRef = useRef(null);

  // 🔁 Fetch jobs for current page
  const fetchJobs = useCallback(() => {
    fetch(`${API_BASE_URL}/api/jobs?page=${page}`)
      .then(res => res.json())
      .then(data => {
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
        setTotalPages(data.totalPages || 1);
      })
      .catch(() => setJobs([]));
  }, [page]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // ✏️ Edit job post using JobForm
  const handleEditClick = (job) => {
    setEditJob(job);
    setTimeout(() => {
      jobFormRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleCancelEdit = () => setEditJob(null);

  // ❌ Delete job post
  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchJobs(); // Refresh job list
    } catch {
      alert('Failed to delete job');
    }
  };

  // 🔃 Refresh job list after posting
  const handleJobPosted = () => {
    fetchJobs();
    if (typeof onPostSuccess === 'function') onPostSuccess();
  };

  // 🖋️ Job description editor state
  //const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  //const [descriptionContent, setDescriptionContent] = useState('');

  // 📝 Launch editor for selected job
  /*const startEditing = (job) => {
    setEditingDescriptionId(job._id);
    setDescriptionContent(job.customDescription || '');
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 150);
  };*/

  // ❌ Cancel editor session
  //const cancelEditing = () => setEditingDescriptionId(null);

  // ✅ Refresh list after saving description
//  const refreshAfterSave = () => {
  //  setEditingDescriptionId(null);
    //fetchJobs();};

  // 🎛️ Filter and sort states
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState('');

  // ♻️ Reset filters
  const handleClearFilters = () => {
    setSelectedStatus('');
    setSelectedDepartment('');
    setSearchKeyword('');
    setSortBy('');
  };

  // 🔍 Filter logic for current jobs
  const filteredJobs = jobs.filter(job => {
    const now = new Date();
    const lastDate = new Date(job.lastDate);
    const status = Math.ceil((lastDate - now) / (1000 * 60 * 60 * 24)) < 0 ? 'Expired' : 'Active';

    const matchStatus = selectedStatus ? status === selectedStatus : true;
    const matchDept = selectedDepartment ? job.department === selectedDepartment : true;
    const matchKeyword = searchKeyword
      ? job.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        job.location.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        job.department.toLowerCase().includes(searchKeyword.toLowerCase())
      : true;

    return matchStatus && matchDept && matchKeyword;
  });

  // 🔃 Sort filtered jobs
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    if (sortBy === 'postedDate') return new Date(b.postedDate) - new Date(a.postedDate);
    if (sortBy === 'lastDate') return new Date(b.lastDate) - new Date(a.lastDate);
    return 0;
  });

  return (
    <>
      {/* ⏳ Auto logout warning */}
      {warningVisible && (
        <div className="logout-warning visible">
          ⏳ You’ve been inactive. Logging out in 1 minute...
        </div>
      )}

      {/* 🖥️ Main dashboard */}
      <div className="dashboard-container">
        <h1>👋 Welcome, {username}</h1>

        {/* 📊 Admin stats */}
        <div className="stats-panel">
          <h2>📊 Dashboard Stats</h2>
          <p>Total Jobs Posted: <strong>{stats.totalJobs}</strong></p>
          <h3>🕒 Recent Jobs:</h3>
          <ul>
            {stats.recentJobs.length > 0 ? (
              stats.recentJobs.map(job => (
                <li key={job._id}>{job.title} — {job.department}</li>
              ))
            ) : (
              <li>No recent jobs found.</li>
            )}
          </ul>
        </div>

        {/* 📝 Job creation/edit form */}
        <JobForm
          ref={jobFormRef}
          token={token}
          onJobPosted={handleJobPosted}
          editData={editJob}
          onCancelEdit={handleCancelEdit}
        />

        {/* 🎛️ Filter controls */}
        <FilterBar
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          selectedDepartment={selectedDepartment}
          setSelectedDepartment={setSelectedDepartment}
          onClear={handleClearFilters}
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        {/* 📋 Job listing table */}
        <h2>🗂️ Manage Posted Jobs</h2>
        {sortedJobs.length > 0 ? (
          <div className="table-wrapper">
            <table className="job-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Last Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map(job => (
                  <tr key={job._id}>
                    <td>{job.title}</td>
                    <td>{job.department}</td>
                    <td>{job.location}</td>
                    <td>{job.lastDate ? new Date(job.lastDate).toLocaleDateString() : '—'}</td>
                    <td>
                      <button onClick={() => handleEditClick(job)}>✏️</button>
                      <button onClick={() => handleDelete(job._id)}>❌</button>
                      {/*{!job.customDescription && <button onClick={() => startEditing(job)}>📝 Add Desc</button>}
                      {job.customDescription && <button onClick={() => startEditing(job)}>📝 Edit Desc</button>}*/}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No jobs found.</p>
        )}

                {/* 🧾 Rich description editor (modular component) 
        {editingDescriptionId && (
          <JobDescriptionEditor
            jobId={editingDescriptionId}
            initialContent={descriptionContent}
            token={token}
            onSave={refreshAfterSave}
            onCancel={cancelEditing}
          />
        )}*/}

        {/* 🔢 Pagination Controls */}
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>⏮️ Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next ⏭️</button>
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;

